import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export default function RouteMap({ stops, userLocation }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || !stops || stops.length === 0) return;

    // Clean up previous map instance
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    // Get valid stops with coordinates
    const validStops = stops.filter(
      (s) => s.latitude && s.longitude && s.latitude !== 0
    );
    if (validStops.length === 0) return;

    // Create map
    const map = L.map(mapRef.current, {
      zoomControl: true,
      attributionControl: false,
    });
    mapInstanceRef.current = map;

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
    }).addTo(map);

    // Create bounds to fit all markers
    const bounds = L.latLngBounds([]);

    // Add numbered winery markers
    validStops.forEach((stop, index) => {
      const latlng = [stop.latitude, stop.longitude];
      bounds.extend(latlng);

      // Custom numbered pin marker (circle + pointer, like Google Maps)
      const icon = L.divIcon({
        className: 'route-marker',
        html: `<div style="display:flex;flex-direction:column;align-items:center;">
          <div style="
            width: 36px; height: 36px;
            background: #722F37;
            border: 3px solid white;
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            color: white; font-weight: bold; font-size: 15px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.5);
          ">${index + 1}</div>
          <div style="
            width: 0; height: 0;
            border-left: 7px solid transparent;
            border-right: 7px solid transparent;
            border-top: 10px solid #722F37;
            margin-top: -1px;
          "></div>
        </div>`,
        iconSize: [36, 46],
        iconAnchor: [18, 46],
        popupAnchor: [0, -48],
      });

      L.marker(latlng, { icon })
        .addTo(map)
        .bindPopup(
          `<div style="font-size: 14px; min-width: 160px;">
            <strong style="color: #722F37; font-size: 13px;">Stop ${index + 1}</strong><br/>
            <span style="font-weight: 700; color: #111;">${stop.name}</span><br/>
            <span style="color: #444; font-size: 12px;">${stop.address || ''}</span>
          </div>`
        );
    });

    // Draw route line connecting stops in order
    const routeCoords = validStops.map((s) => [s.latitude, s.longitude]);
    L.polyline(routeCoords, {
      color: '#722F37',
      weight: 3,
      opacity: 0.7,
      dashArray: '8, 8',
    }).addTo(map);

    // Add user location marker if available
    if (userLocation && userLocation.lat && userLocation.lng) {
      const userLatLng = [userLocation.lat, userLocation.lng];
      bounds.extend(userLatLng);

      const userIcon = L.divIcon({
        className: '',
        html: `<div style="display:flex;flex-direction:column;align-items:center;overflow:visible;width:80px;position:relative;left:-32px;top:-8px;">
          <div style="width:16px;height:16px;background:#4285F4;border:3px solid white;border-radius:50%;box-shadow:0 0 0 2px #4285F4,0 2px 6px rgba(0,0,0,0.3);"></div>
          <div style="margin-top:2px;background:#4285F4;color:white;font-size:10px;font-weight:700;padding:2px 7px;border-radius:8px;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.3);line-height:14px;">You are here</div>
        </div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });

      L.marker(userLatLng, { icon: userIcon })
        .addTo(map)
        .bindPopup('<strong>Your Location</strong>');
    }

    // Fit map to show all markers with padding
    map.fitBounds(bounds, { padding: [30, 30] });

    // Cleanup on unmount
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [stops, userLocation]);

  return (
    <div
      ref={mapRef}
      style={{ width: '100%', height: '100%', minHeight: '300px' }}
    />
  );
}
